export type BillingEmailDraftDecision =
  | {
      kind: "noop";
      /**
       * Billing email to use for subsequent validation/checkout calls.
       * May be an empty string if nothing is set (server will fall back).
       */
      billingEmail: string;
    }
  | {
      kind: "save";
      /**
       * Normalized (trimmed) billing email to persist before continuing.
       */
      billingEmail: string;
    }
  | { kind: "invalid_empty" }
  | { kind: "invalid_format" };

function normalizeForCompare(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Lightweight validation to avoid firing backend mutations for obviously bad
 * inputs. Backend still enforces the canonical validation rules.
 */
export function isPlausibleBillingEmail(email: string): boolean {
  const trimmed = email.trim();
  if (!trimmed) return false;
  const at = trimmed.indexOf("@");
  // "a@b.co" is the shortest-ish plausible.
  if (at <= 0) return false;
  if (at >= trimmed.length - 4) return false;
  return true;
}

export function decideBillingEmailDraftAction(input: {
  savedBillingEmail: string;
  draftBillingEmail: string | null;
}): BillingEmailDraftDecision {
  const saved = input.savedBillingEmail ?? "";

  if (input.draftBillingEmail === null) {
    return { kind: "noop", billingEmail: saved.trim() };
  }

  const candidate = input.draftBillingEmail.trim();
  if (!candidate) return { kind: "invalid_empty" };
  if (!isPlausibleBillingEmail(candidate)) return { kind: "invalid_format" };

  const savedLower = normalizeForCompare(saved);
  const candidateLower = normalizeForCompare(candidate);
  if (savedLower === candidateLower) {
    // The user typed something but it doesn't materially differ from the saved value.
    return { kind: "noop", billingEmail: candidate };
  }

  return { kind: "save", billingEmail: candidate };
}
